import { useCallback } from 'react';
import { useFormState, type FieldValues, type UseFormReturn } from 'react-hook-form';

import { useSaveBarEntry, type SaveBarEntryHandle } from './use-save-bar-entry';

export interface UseSaveBarOptions<TFieldValues extends FieldValues, TContext, TTransformedValues> {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  /** The form's submit path, usually the result of `form.handleSubmit(...)`. */
  onSave: () => unknown;
  /** Defaults to `form.reset()`, back to the default values. */
  onDiscard?: () => void;
  /** Pending state the form does not know about, e.g. `mutation.isPending`. */
  saving?: boolean;
  /** Ask "Discard all unsaved changes?" before discarding. Off by default. */
  confirmDiscard?: boolean;
  /** Keep the form out of the bar, e.g. while the person may only read. */
  disabled?: boolean;
}

/**
 * Puts a react-hook-form form under the save bar: the bar appears when the form is dirty, its
 * Save button and Ctrl+S run `onSave`, Discard resets the form, and leaving the page is refused
 * until one of them happened. After a successful save call `form.reset(values)` as usual so the
 * form is pristine again and the bar goes away.
 */
export function useSaveBar<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  form,
  onSave,
  onDiscard,
  saving = false,
  confirmDiscard,
  disabled,
}: UseSaveBarOptions<TFieldValues, TContext, TTransformedValues>): SaveBarEntryHandle {
  const { isDirty, isSubmitting } = useFormState({ control: form.control });
  const reset = form.reset;
  const discard = useCallback(() => {
    if (onDiscard) onDiscard();
    else reset();
  }, [onDiscard, reset]);

  return useSaveBarEntry({
    dirty: isDirty,
    saving: saving || isSubmitting,
    onSave,
    onDiscard: discard,
    confirmDiscard,
    disabled,
  });
}
